import React, {useEffect, useMemo, useState} from "react";

import DateBox from "devextreme-react/date-box";

import {
  BarGauge,
  Label as BarGaugeLabel,
  Export,
} from "devextreme-react/bar-gauge";

import Chart, {
  Series,
  Legend,
  ArgumentAxis,
  ValueAxis,
  CommonSeriesSettings,
  Tooltip, Format,
} from "devextreme-react/chart";

import PieChart, {
  Series as PieSeries,
  Label,
  Connector,
  Legend as PieLegend,
} from "devextreme-react/pie-chart";

import "./PnLDashboard.css";
import {PnLPerformanceDashboardChart} from "./PnLCharts";
import BudgetChart from "./BudgetChart";

import { toDate } from '../../utils/dateUtils';
import useCobDate from '../../hooks/useCobDate';
import { baseUrl } from '../../hooks/useFetch';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface PnLRow {
  pnl_mtd: number | null;
  pnl_fytd: number | null;
  sharpe_ratio: number | null;
  max_peak_trough_dd: number | null;
}

interface BudgetRow {
  budget_perc: number | null;
}

interface PieItem {
  category: string;
  value: number;
}

interface QuarterRow {
  quarter: string;
  quarterLabel?: string;
  'Total RMS': number;
  'Market Maker Total': number;
  'Sales Total': number;
  'France': number;
  'Market: Voluntary Carbon': number;
}

interface QuarterRawObj {
  element_name: Record<string, string>;
  fiscal_quarter: Record<string, string>;
  pnl_quarter: Record<string, number>;
}

interface DashboardPayload {
  'RMS P&L': {
    element_name: Record<string, string>;
    pnl_mtd: Record<string, number>;
    pnl_fytd: Record<string, number>;
    sharpe_ratio: Record<string, number>;
    max_peak_trough_dd: Record<string, number>;
  };
  'Market Maker P&L': {
    element_name: Record<string, string>;
    pnl_mtd: Record<string, number>;
    pnl_fytd: Record<string, number>;
    sharpe_ratio: Record<string, number>;
    max_peak_trough_dd: Record<string, number>;
  };
  'Sales P&L': {
    element_name: Record<string, string>;
    pnl_fytd: Record<string, number>;
  };
  budget_pnl: {
    element_name: Record<string, string>;
    fytd_vs_budget_pct: Record<string, number>;
  };
  quarterly_pnl: QuarterRawObj;
}

interface FYInfo {
  fyStartYear: number;
  fyEndYear: number;
  calendarYY: number;
  label: string;
  fiscalYY: string;
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

const PnLDashboard: React.FC = () => {
  const [cobDate, setCobDate, , latestCobDate] = useCobDate("/pnl_latest_cob");
  const [payload, setPayload] = React.useState<DashboardPayload | null>(null);
  const [rmsPnLTotal, setRmsPnLTotal] = React.useState<Record<string, PnLRow> | null>(null);
  const [mmPnLTotal, setMmPnLTotal] = React.useState<Record<string, PnLRow> | null>(null);
  const [budgetPnL, setBudgetPnL] = React.useState<Record<string, BudgetRow> | null>(null);
  const [quarterlyPnL, setQuarterlyPnL] = React.useState<QuarterRow[] | null>(null);
  const [marketMakerPie, setMarketMakerPie] = React.useState<PieItem[] | null>(null);
  const [salesPie, setSalesPie] = React.useState<PieItem[] | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  // Mock data

  const salesData = [
    { category: "Carbon Sales", value: 64 },
    { category: "Structured Sales", value: 23 },
    { category: "Feedstock Sales", value: 8 },
    { category: "Gas & Power Sales", value: 3 },
    { category: "Other", value: 2 },
  ];

React.useEffect(() => {
  const ctrl = new AbortController();
  (async () => {
    try {
      if (!cobDate) return;
      const formattedSelectedDate = cobDate.toISOString().split('T')[0];
      const res = await fetch(`${baseUrl}/pnl_dashboard?cob_date=${formattedSelectedDate}`, { signal: ctrl.signal });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data: DashboardPayload = await res.json();
      setPayload(data);

      let rms_pnl = data['RMS P&L']
      const rowsByName: Record<string, PnLRow> = Object.fromEntries(
      Object.keys(rms_pnl.element_name).map(k => [
        rms_pnl.element_name[k],
        {
          pnl_mtd: rms_pnl.pnl_mtd[k],
          pnl_fytd: rms_pnl.pnl_fytd[k],
          sharpe_ratio: rms_pnl.sharpe_ratio[k],
          max_peak_trough_dd: rms_pnl.max_peak_trough_dd[k],
        },

      ])
      );
      setRmsPnLTotal(rowsByName);

     let srcMM = data["Market Maker P&L"];

      const rowsByNameMM: Record<string, PnLRow> = Object.fromEntries(
        Object.keys(srcMM.element_name)
          .map((k) => {
            const name = srcMM.element_name[k];
            return [
              name,
              {
                pnl_mtd: srcMM.pnl_mtd[k],
                pnl_fytd: srcMM.pnl_fytd[k],
                sharpe_ratio: srcMM.sharpe_ratio[k],
                max_peak_trough_dd: srcMM.max_peak_trough_dd[k],
              },
            ];
          })
          //.filter(([name]) => name !== "France") // remove France
      );

      setMmPnLTotal(rowsByNameMM);

      let budget_pnl = data['budget_pnl']
      const budgetRows: Record<string, BudgetRow> = Object.fromEntries(
      Object.keys(budget_pnl.element_name).map(k => [
        budget_pnl.element_name[k],
        {
          budget_perc: budget_pnl.fytd_vs_budget_pct[k],
        },

      ])
      );
      setBudgetPnL(budgetRows);

      let quarterly_pnl = toQuarterData(data['quarterly_pnl']);

      setQuarterlyPnL(quarterly_pnl);


         let mmPie: PieItem[] = Object.keys(srcMM.element_name)
      .filter(k => {
        const name = srcMM.element_name[k].replace(/^Market:\s*/, "");
        return name !== "Market Maker Total" && name !== "France";
      })
      .map(k => {
        const name = srcMM.element_name[k]
          .replace(/^Market:\s*/, "")
          .replace("Biofuels and Oil", "Biofuels & Oil");

        return {
          category: name,
          value: formatMMPie(srcMM.pnl_fytd[k]),
        };
      });

    setMarketMakerPie(mmPie);


      let srcSales  = data["Sales P&L"]
      let salesPieData: PieItem[] = Object.keys(srcSales.element_name)
                        .filter(k => srcSales.element_name[k] !== "Sales Total")
        .map(k => {
          const name = srcSales.element_name[k]
          return { category: name, value: formatMMPie(srcSales.pnl_fytd[k]) };
        });

      setSalesPie(salesPieData);

    } catch (err: any) {
      if (err.name !== "AbortError") console.error("Failed to load PnL data", err);
    }
  })();
  return () => ctrl.abort();
}, [baseUrl,cobDate]);
 const salesTotal = useMemo(() => rmsPnLTotal?.["Sales Total"], [rmsPnLTotal]);
 const marketMakerTotal = useMemo(() => rmsPnLTotal?.["Market Maker Total"], [rmsPnLTotal]);
 const franceTotal = useMemo(() => mmPnLTotal?.["France"], [rmsPnLTotal]);
 const vcTotal = useMemo(() => mmPnLTotal?.["Market: Voluntary Carbon"], [rmsPnLTotal]);
 const rmsTotal = useMemo(() => rmsPnLTotal?.["Total RMS"], [rmsPnLTotal]);
 const budgetRMS = useMemo(() => budgetPnL?.["TOTAL RMS"], [budgetPnL]);
 const budgetSales = useMemo(() => budgetPnL?.["SALES TOTAL"], [budgetPnL]);
 const budgetMarketMaker = useMemo(() => budgetPnL?.["MARKET MAKER TOTAL"], [budgetPnL]);
 const budgetFrance = useMemo(() => budgetPnL?.["FRANCE"], [mmPnLTotal]);
 const marketVoluntaryCarbon = useMemo(() => budgetPnL?.["Market: Voluntary Carbon"], [mmPnLTotal]);

 function ukFinancialYear(
  date: Date | null = cobDate,
  { timeZone = "Europe/London", startMonth = 4, startDay = 1 } = {} // startMonth=4 => April (1-based)
): FYInfo {
  const dateFallback = date ?? new Date();
  // Get y/m/d in the target timezone to avoid off-by-one issues
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone,
    year: "numeric",
    month: "numeric",
    day: "numeric",
  }).formatToParts(dateFallback);

  const y = Number(parts.find(p => p.type === "year")!.value);
  const m = Number(parts.find(p => p.type === "month")!.value);
  const d = Number(parts.find(p => p.type === "day")!.value);

  const started = (m > startMonth) || (m === startMonth && d >= startDay);

  const fyStartYear = started ? y : y - 1;        // e.g., 2025
  const fyEndYear   = fyStartYear + 1;            // e.g., 2026
  const calendarYY        = fyStartYear % 100;          // e.g., 25
  const label       = `${fyStartYear}-${String(fyEndYear).slice(-2)}`; // "2025-26"
   const fiscalYY = `${String(fyEndYear).slice(-2)}`;

  return { fyStartYear, fyEndYear, calendarYY, label,fiscalYY };
}
 const fy = ukFinancialYear();
function toQuarterData(obj: QuarterRawObj): QuarterRow[] {
  const idxs = Object.keys(obj.element_name);

  const rows = idxs.map(k => ({
    name: obj.element_name[k],
    // normalize to a stable code like "Q1"
    qCode: String(obj.fiscal_quarter[k]),
    pnl: Number(obj.pnl_quarter[k]) || 0,
  }));

  const qOrder = ['Q1','Q2','Q3','Q4'];
  const qLabels: Record<string, string> = {
    Q1: 'Q1 (Apr 1 - Jun 30)',
    Q2: 'Q2 (Jul 1 - Sep 30)',
    Q3: 'Q3 (Oct 1 - Dec 31)',
    Q4: 'Q4 (Jan 1 - Mar 31)',
  };

  const initRow = (q: string): QuarterRow => ({
    quarter: qLabels[q],                // pretty label for display
    'Total RMS': 0,
    'Market Maker Total': 0,
    'Sales Total': 0,
    'France': 0,
     'Market: Voluntary Carbon':0,
  });

  const agg: Record<string, QuarterRow> = Object.fromEntries(qOrder.map(q => [q, initRow(q)]));

  for (const r of rows) {
    if (agg[r.qCode] && r.name in agg[r.qCode]) {
      (agg[r.qCode] as any)[r.name] += r.pnl;
    }
  }

  return qOrder.map(q => agg[q]);
}

 const fmtEUR2 = new Intl.NumberFormat('en-GB', {
  style: 'currency',
  currency: 'EUR',
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});

function formatEURMM(n: number | null | undefined): string {
  if (n == null || Number.isNaN(n)) return '\u2014';
  const mm = n / 1_000_000;
  return `${fmtEUR2.format(mm)}\u00A0MM`;
}
function formatMMPie(n: number | null | undefined): number {
  if (n == null || !Number.isFinite(n)) return 0;
  const mm = n / 1e6;
  const r = Math.round((mm + Number.EPSILON) * 10) / 10;
  return Object.is(r, -0) ? 0 : r;
}

const fmt2dp = new Intl.NumberFormat('en-GB', {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
})
function format2dp(n: number | null | undefined): string {
  if (n == null || Number.isNaN(n)) return '\u2014';
  return fmt2dp.format(n);
}
function toYMD(value: Date | string | null | undefined): string | null {
  if (!value) return null;
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return null;

  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}
const argMeta = React.useMemo(() => {
  const m: Record<string, string | undefined> = {};
  (quarterlyPnL || []).forEach(d => { m[d.quarter] = d.quarterLabel; });
  return m;
}, [quarterlyPnL]);
  return (
    <div className="dashboard-container-pnl">
      {/* CoB */}
      <div className="cob-section">
        <label><strong>CoB:</strong></label>
        <DateBox
          value={cobDate}
          onValueChanged={(e: any) => setCobDate(e.value)} displayFormat="dd/MM/yyyy"
          width={180}
          max={latestCobDate ?? undefined}
        />
      </div>

      {/* Summary + Gauge Row */}
      <div className="top-row">
        {/* Summary Section */}
        <div className="summary-grid">
  {/* RMS full width on top */}
  <div className="summary-box rms-box">
    <h3>RMS P&amp;L</h3>
    <p>MTD: {rmsTotal?.pnl_mtd != null ? formatEURMM(rmsTotal.pnl_mtd) : '\u2014'}</p>
    <p>FYTD:{rmsTotal?.pnl_fytd != null ? formatEURMM(rmsTotal.pnl_fytd) : '\u2014'}</p>
    <p>Sharpe:{format2dp(rmsTotal?.sharpe_ratio)}</p>
    <p>Max Peak to trough drawdown:{rmsTotal?.max_peak_trough_dd !=null ? formatEURMM(rmsTotal.max_peak_trough_dd):  "\u2014"}</p>
  </div>

  {/* Sales bottom right */}
  <div className="summary-box">
    <h3>Sales P&amp;L</h3>
    <p>MTD:{salesTotal?.pnl_mtd != null ? formatEURMM(salesTotal.pnl_mtd) : '\u2014'}</p>
    <p>FYTD:{salesTotal?.pnl_fytd != null ? formatEURMM(salesTotal.pnl_fytd) : '\u2014'}</p>
      <p>Sharpe:{format2dp(salesTotal?.sharpe_ratio)}</p>
    <p>Max Peak to trough drawdown:{salesTotal?.max_peak_trough_dd !=null ? formatEURMM(salesTotal.max_peak_trough_dd):  "\u2014"}</p>
  </div>

  <div className="summary-box">
    <h3>France P&amp;L</h3>
    <p>MTD:{franceTotal?.pnl_mtd != null ? formatEURMM(franceTotal.pnl_mtd) : '\u2014'}</p>
    <p>FYTD:{franceTotal?.pnl_fytd != null ? formatEURMM(franceTotal.pnl_fytd) : '\u2014'}</p>
    <p>Sharpe:{format2dp(franceTotal?.sharpe_ratio)}</p>
    <p>Max Peak to trough drawdown:{franceTotal?.max_peak_trough_dd !=null ? formatEURMM(franceTotal.max_peak_trough_dd):  "\u2014"}</p>
  </div>
            <div className="summary-box">
    <h3>Voluntary Carbon P&amp;L</h3>
    <p>MTD: {vcTotal?.pnl_mtd != null ? formatEURMM(vcTotal.pnl_mtd) : '\u2014'}</p>
    <p>FYTD:{vcTotal?.pnl_fytd != null ? formatEURMM(vcTotal.pnl_fytd) : '\u2014'}</p>
    <p>Sharpe:{format2dp(vcTotal?.sharpe_ratio)}</p>
    <p>Max Peak to trough drawdown:{vcTotal?.max_peak_trough_dd !=null ? formatEURMM(vcTotal.max_peak_trough_dd):  "\u2014"}</p>
  </div>
  <div className="summary-box">
    <h3>Market Maker P&amp;L</h3>
    <p>MTD: {marketMakerTotal?.pnl_mtd != null ? formatEURMM(marketMakerTotal.pnl_mtd) : '\u2014'}</p>
    <p>FYTD:{marketMakerTotal?.pnl_fytd != null ? formatEURMM(marketMakerTotal.pnl_fytd) : '\u2014'}</p>
    <p>Sharpe:{format2dp(marketMakerTotal?.sharpe_ratio)}</p>
    <p>Max Peak to trough drawdown:{marketMakerTotal?.max_peak_trough_dd !=null ? formatEURMM(marketMakerTotal.max_peak_trough_dd):  "\u2014"}</p>
  </div>

</div>

        {/* Gauge */}
        <div className="chart-box gauge-box two-charts">
  <h4 className="chart-title">FY-{fy.fiscalYY} FYTD P&amp;L vs Budget</h4>

  <div className="two-charts-body">
    {/* LEFT PANE: Gauge + legend */}
    <div className="chart-pane">
      <BarGauge
        id="gauge"
        startValue={0}
        endValue={120}

         relativeInnerRadius={0.65}
        barSpacing={12}
        values={[
          format2dp(budgetFrance?.budget_perc) as any,
          format2dp(budgetRMS?.budget_perc) as any,
          format2dp(budgetMarketMaker?.budget_perc) as any,
          format2dp(budgetSales?.budget_perc) as any,
          format2dp(marketVoluntaryCarbon?.budget_perc) as any,
        ]}

        geometry={{ startAngle: 180, endAngle: 360 }}
        palette={["#43A047", "#03A9F4", "#E53935", "#8D6E63"]}
          size={{ width: "100%" as any, height: 260 }}

        resolveLabelOverlapping="shift"
      >
        <BarGaugeLabel
          indent={30}
          format={{ type: "fixedPoint", precision: 1 }}
          customizeText={({ valueText }: { valueText: string }) => `${valueText} %`}
        />
        <Export enabled={false} />
      </BarGauge>

      {/* Legend for Gauge */}
      <div className="legend legend-compact">
        <div className="legend-item">
          <div className="legend-color" style={{ backgroundColor: "#43A047" }} />
          <span>France</span>
        </div>
        <div className="legend-item">
          <div className="legend-color" style={{ backgroundColor: "#03A9F4" }} />
          <span>RMS</span>
        </div>
        <div className="legend-item">
          <div className="legend-color" style={{ backgroundColor: "#E53935" }} />
          <span>Market Maker</span>
        </div>
        <div className="legend-item">
          <div className="legend-color" style={{ backgroundColor: "#8D6E63" }} />
          <span>Sales</span>
        </div>
      </div>
    </div>

    {/* RIGHT PANE: Chart 2 + legend */}
    <div className="chart-pane">
      {/*<h4 className="sub-title">Chart 2 title</h4>*/}

      {/* Replace this with your second chart */}
      <div className="chart2-placeholder">
        <BudgetChart cobDate={cobDate}></BudgetChart>
      </div>

    </div>
  </div>
</div>

        </div>




      {/* Charts */}
      <div className="charts-section">
        {/* Line Chart */}
        <div className="chart-box">
          <h4>RMS FYTD P&amp;L (€mm)</h4>
          <PnLPerformanceDashboardChart page="dashboard" cob_date={cobDate}></PnLPerformanceDashboardChart>
          {/*<PnLCharts page="dashboard" cob_date={cobDate}></PnLCharts>*/}
          {/*<Chart dataSource={rmsHistory}>*/}
          {/*  <CommonSeriesSettings argumentField="date" type="line" />*/}
          {/*  <Series valueField="value" name="RMS P&L" />*/}
          {/*  <ArgumentAxis />*/}
          {/*  <ValueAxis />*/}
          {/*  <Legend visible={false} />*/}
          {/*  <Tooltip enabled={true} zIndex={2147483000} />*/}
          {/*</Chart>*/}
        </div>

        {/* Quarterly P&L */}
        <div className="chart-box">
          <h4>FY-{fy.fiscalYY} Quarterly P&amp;L (€mm)</h4>
          <Chart dataSource={quarterlyPnL}>
  <CommonSeriesSettings argumentField="quarter" type="bar">
    <Label
      visible={true}
      customizeText={(e: any) =>
        Math.abs(e.value) <= 1e-6
          ? ""
          : (e.value / 1_000_000).toLocaleString(undefined, { maximumFractionDigits: 1 })
      }
    >
      <Format type="fixedPoint" precision={0} />
    </Label>
  </CommonSeriesSettings>

  <Series valueField="Total RMS" name="RMS" />
  <Series valueField="Market Maker Total" name="Market Maker" />
  <Series valueField="Sales Total" name="Sales" />
  <Series valueField="France" name="France" />
  <Series valueField="Market: Voluntary Carbon" name="Voluntary Carbon" />

  <ArgumentAxis>
    <Label
      {...{ overlappingBehavior: "stagger" } as any}
      customizeText={(e: any) => `${e.value}\n${argMeta[e.value] ?? ""}`}
    />
  </ArgumentAxis>

  <ValueAxis>
    <Label
      customizeText={(e: any) =>
        (e.value / 1_000_000).toLocaleString(undefined, { maximumFractionDigits: 2 })
      }
    />
  </ValueAxis>

  <Legend
    visible={true}
    verticalAlignment="bottom"
    horizontalAlignment="center"
    orientation="horizontal"
    columnCount={3}
    itemTextPosition="right"
    markerSize={12}
    font={{ size: 12 }}
    margin={{ top: 6, bottom: 0, left: 0, right: 0 }}
    paddingLeftRight={6}
    paddingTopBottom={6}
  />

  <Tooltip enabled={true} zIndex={2147483000} />
</Chart>

        </div>

        {/* Market Maker Pie */}
        <div className="chart-box">
          <h4>Market Maker FYTD P&amp;L ({marketMakerTotal?.pnl_fytd != null ? formatEURMM(marketMakerTotal.pnl_fytd) : '\u2014' })</h4>
          <PieChart dataSource={marketMakerPie} palette="Dark Violet">
            <PieSeries argumentField="category" valueField="value">
              <Label visible={true} position={"columns"} radialOffset={1}  {...{ resolveLabelOverlapping: "shift" } as any}
              customizeText={(e: any) =>
        `${e.argumentText}: ${e.percentText} (${Number(e.value).toLocaleString(undefined, { maximumFractionDigits: 1 })})`
      }
              >
                <Connector visible={true}  />
              </Label>
            </PieSeries>
            <PieLegend
              visible={false}
              horizontalAlignment="center"
              verticalAlignment="bottom"
              font={{ size: 14 }}
              markerSize={20}

            />
          </PieChart>
        </div>

        {/* Sales Pie */}
        <div className="chart-box">
          <h4>Sales FYTD P&amp;L ({salesTotal?.pnl_fytd != null ? formatEURMM(salesTotal.pnl_fytd) : '\u2014' })</h4>
          <PieChart dataSource={salesPie} palette="Dark Violet">
            <PieSeries argumentField="category" valueField="value">
              <Label visible={true} position={"columns"} radialOffset={1}  {...{ resolveLabelOverlapping: "shift" } as any}
              customizeText={(e: any) =>
        `${e.argumentText}: ${e.percentText} (${Number(e.value).toLocaleString(undefined, { maximumFractionDigits: 1 })})`
      }
              >
                <Connector visible={true}  />
              </Label>
            </PieSeries>
            <PieLegend
              visible={false}
              horizontalAlignment="center"
              verticalAlignment="bottom"
              font={{ size: 14 }}
              markerSize={20}
            />
          </PieChart>
        </div>
      </div>
    </div>
  );
};

export default PnLDashboard;
